import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify"; // npm install dompurify
import "./ManageCompanies.css";
import Footer from "./footer";

const API_BASE = "https://api.niveshly.com";

const ManageCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState({
    name: "",
    logo: "",
    price: "",
    tag: "",
    quantity: "",
    isin: "",
    pan: "",
    description: "",
    trending: false,
    availability: true,
  });
  const [editIndex, setEditIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sanitize HTML
  const sanitizeHTML = (html) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'strike',
        'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'blockquote', 'code', 'pre', 'span', 'div', 'a'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'title', 'style'],
    });
  };

  // Fetch all companies
  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE}/companies`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setCompanies(data);
    } catch (err) {
      console.error("Failed to fetch companies:", err);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Add or Update company
  const handleSaveCompany = async () => {
    const { name, logo, price, tag, quantity, isin, pan, description } = newCompany;

    if (!name || !logo || !price || !tag || !quantity || !isin || !pan) {
      alert("Please fill all required fields!");
      return;
    }

    // Sanitize description before saving
    const sanitizedDescription = sanitizeHTML(description);

    try {
      const payload = { ...newCompany, description: sanitizedDescription };

      if (editIndex !== null) {
        const id = companies[editIndex].id;
        const res = await fetch(`${API_BASE}/companies/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Update failed");
        alert("Company updated successfully!");
        setEditIndex(null);
      } else {
        const res = await fetch(`${API_BASE}/companies`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Create failed");
        alert("Company added successfully!");
      }

      // Reset form
      setNewCompany({
        name: "",
        logo: "",
        price: "",
        tag: "",
        quantity: "",
        isin: "",
        pan: "",
        description: "",
        trending: false,
        availability: true,
      });
      fetchCompanies();
    } catch (err) {
      alert(`Error saving company: ${err.message}`);
      console.error(err);
    }
  };

  // Delete company
  const handleDeleteCompany = async (id) => {
    if (!window.confirm("Are you sure you want to delete this company?")) return;
    try {
      const res = await fetch(`${API_BASE}/companies/${id}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error("Delete failed");
      fetchCompanies();
      alert("Company deleted successfully!");
    } catch (err) {
      alert("Failed to delete company.");
      console.error(err);
    }
  };

  // Edit company
  const handleEditCompany = (index) => {
    setEditIndex(index);
    setNewCompany(companies[index]);
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditIndex(null);
    setNewCompany({
      name: "",
      logo: "",
      price: "",
      tag: "",
      quantity: "",
      isin: "",
      pan: "",
      description: "",
      trending: false,
      availability: true,
    });
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("sheet_name", "Sheet1");

    try {
      const res = await fetch(`${API_BASE}/companies/bulk/upsert`, {
        method: "PUT",
        body: formData,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || "Upload failed");

      let message =
        `Upload Successful!\n` +
        `Inserted: ${result.inserted}\n` +
        `Updated: ${result.skipped}`;

      if (result.errors?.length) {
        message += `\n\nErrors:\n${result.errors.join("\n")}`;
      }

      alert(message);
      fetchCompanies();
      fileInputRef.current.value = "";
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };


  return (
    <div className="manage-container">
      <header className="manage-header">
        <img
          src="WhatsApp_Image_2025-02-10_at_10.54.09_cf865204-removebg-preview.webp"
          alt="Logo"
          className="logo"
        />
        <h1>Manage Companies</h1>
        <button
          className="details-nav-btn"
          onClick={() => navigate("/company-details")}
        >
          Manage Company Details →
        </button>
      </header>

      {/* Excel Upload Section */}
      <div className="upload-section">
        <h2>Bulk Upload via Excel</h2>
        <div className="upload-controls">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleExcelUpload}
            disabled={uploading}
          />
          <button className="upload-btn" disabled={uploading}>
            {uploading ? "Uploading..." : "Upload Excel"}
          </button>
        </div>
        <p className="upload-hint">
          <strong>Sheet Name:</strong> Sheet1
          <br />
          Required columns: <strong>name, logo, price, tag, quantity, isin, pan</strong>
          <br />
          Optional: <em>description, trending (0/1), availability (0/1)</em>
          <br />
          <strong>Note:</strong> Description column supports HTML tags
        </p>
      </div>

      {/* Form Section */}
      <div className="form-section">
        <h2>{editIndex !== null ? "Edit Company" : "Add New Company"}</h2>

        {["name", "logo", "price", "tag", "quantity", "isin", "pan"].map((field) => (
          <input
            key={field}
            type="text"
            placeholder={field.charAt(0).toUpperCase() + field.slice(1).toUpperCase()}
            value={newCompany[field]}
            onChange={(e) =>
              setNewCompany({ ...newCompany, [field]: e.target.value })
            }
            required
          />
        ))}

        {/* HTML Description Field */}
        <div className="html-editor">
          <label>
            <strong>Company Description (HTML Supported)</strong>
          </label>
          <textarea
            placeholder={`Enter HTML formatted description:
Examples:
<p><strong>Bold text</strong> and <em>italic text</em></p>
<ul>
  <li>Bullet point 1</li>
  <li>Bullet point 2</li>
</ul>
<h3>Heading</h3>
<p>Visit <a href="https://example.com" target="_blank">our website</a></p>`}
            value={newCompany.description}
            onChange={(e) =>
              setNewCompany({ ...newCompany, description: e.target.value })
            }
            rows={10}
            className="html-textarea"
          />
          <div className="html-hint">
            <small>✅ Supported: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;h1&gt;-&lt;h6&gt;, &lt;a href&gt;</small>
          </div>
        </div>

        {/* Boolean fields */}
        <div className="boolean-fields">
          <label>
            <input
              type="checkbox"
              checked={newCompany.trending}
              onChange={(e) =>
                setNewCompany({ ...newCompany, trending: e.target.checked })
              }
            />
            Trending
          </label>
          <label>
            <input
              type="checkbox"
              checked={newCompany.availability}
              onChange={(e) =>
                setNewCompany({ ...newCompany, availability: e.target.checked })
              }
            />
            Available
          </label>
        </div>

        <div className="form-buttons">
          <button className="save-btn" onClick={handleSaveCompany}>
            {editIndex !== null ? "Update Company" : "Add Company"}
          </button>
          {editIndex !== null && (
            <button className="cancel-btn" onClick={handleCancelEdit}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* List Section */}
      <div className="company-list">
        <h2>Company List ({companies.length})</h2>
        {companies.length === 0 ? (
          <p className="empty-text">No companies added yet.</p>
        ) : (
          companies.map((company, index) => (
            <div key={company.id} className="company-card">
              <div className="company-header">
                <img
                  src={company.logo}
                  alt={company.name}
                  className="company-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div>
                  <h3>{company.name}</h3>
                  <span className={`status ${company.availability ? 'available' : 'unavailable'}`}>
                    {company.availability ? "Available" : "Unavailable"}
                  </span>
                </div>
              </div>

              <div className="company-details">
                <p><strong>Price:</strong> ₹{company.price}</p>
                <p><strong>Sector:</strong> {company.tag}</p>
                <p><strong>Quantity:</strong> {company.quantity}</p>
                <p><strong>ISIN:</strong> {company.isin}</p>
                <p><strong>PAN:</strong> {company.pan}</p>
                <p><strong>Trending:</strong> {company.trending ? "🔥 Yes" : "❌ No"}</p>
              </div>

              {/* Render HTML Description Safely */}
              {company.description && (
                <div className="description-html">
                  <strong>Description:</strong>
                  <div
                    className="html-content"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(company.description),
                    }}
                  />
                </div>
              )}

              <div className="action-buttons">
                <button
                  className="edit-btn"
                  onClick={() => handleEditCompany(index)}
                >
                  Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteCompany(company.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ManageCompanies;